"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

import { cn } from "@repo/ui/lib/utils";

/**
 * Renders a showcase source logo (36px unless `className` sizes it), falling back to a letter-avatar tile
 * when there is no src or the image fails to load. Source logos are often SVG
 * (which next/image's optimizer rejects with a 400) and storage ids can dangle
 * after an image is removed — both would otherwise show a broken-image icon. We
 * serve the raw bytes (`unoptimized`, trivial cost at 36px) and render the
 * fallback on any remaining load error.
 */
export function ShowcaseSourceImage({
  src,
  name,
  className,
}: {
  src?: string;
  name: string;
  className: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (!src || failed) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600/20 text-sm font-bold text-blue-400",
          className,
        )}
      >
        {name.charAt(0)}
      </span>
    );
  }
  return (
    <Image
      src={src}
      alt=""
      width={36}
      height={36}
      unoptimized
      onError={() => setFailed(true)}
      className={className}
    />
  );
}
